// ----- chPopover Utils -------------------------------------------------------

import {
  ARROW_OFFSET_ADJUSTMENT,
  EDGE_OVERLAP_ATTACHMENTS,
  SIDES
} from 'etc/constants';


/**
 * Converts a string to kebab case.
 *
 * @example
 *
 * kebabString('fooBar') //=> 'foo-bar'
 *
 * @param  {string} str
 * @return {string}
 */
export function kebabString(str) {
  return str.replace(/([A-Z])/g, char => '-' + char.toLowerCase());
}


/**
 * Returns the first element in the provided array.
 *
 * @param  {array} xs
 * @return {*}
 */
export function head(xs) {
  if (Array.isArray(xs)) {
    return xs[0];
  }
}


/**
 * Returns the last element of the provided array.
 *
 * @param  {array} xs
 * @return {*}
 */
export function last(xs) {
  if (Array.isArray(xs)) {
    return xs[xs.length - 1];
  }
}


/**
 * Converts degrees to radians.
 *
 * @param  {number} deg
 * @return {number}
 */
export function degToRad(deg) {
  return deg * (Math.PI / 180);
}


/**
 * Converts radians to degrees.
 *
 * @param  {number} rads
 * @return {number}
 */
export function radToDeg(rads) {
  return rads * (180 / Math.PI);
}


/**
 * A mean function, yo.
 *
 * @example
 *
 * mean(4, 52, 72) //=> 43
 *
 * @param  {arglist} args - Numbers to calculate the mean of.
 * @return {number}
 */
export function mean(...args) {
  if (!args.length) {
    return 0;
  }

  const sum = args.reduce((accumulator, value) => accumulator + value);
  return sum / args.length;
}


/**
 * Provided an object containing a set of CSS rules and a jQuery/jqLite element,
 * applies the styles to the element.
 *
 * @example
 *
 * applyStyle({
 *   display: 'block'
 * }, someEl);
 *
 * @param  {object} styles - CSS styles to apply.
 * @param  {object} el - Element to apply styles to.
 */
export function applyStyle(styles, el) {
  Object.keys(styles).forEach(property => {
    const rule = styles[property];

    el.css(property, rule);
  });
}


/**
 * Wrapper struct for simplifying basic arithmetic with CSS values. Instances
 * can be used directly in mathematical operations, and the original string
 * value (with unit) can be obtained with .value(). The left-hand unit will be
 * favored when both operands' units are truthy.
 *
 * @example
 *
 * let a = cssValue('4px');
 * let b = cssValue('3px');
 * let c = cssValue(5);
 *
 * Math.pow(a, b)        //=> 64
 * a.add(b).value()      //=> '7px'
 * a.subtract(c).value() //=> '-1px'
 * c.add(b).value()      //=> '8px'
 * a.multiply(2).value() //=> '10'
 * b.pow(2).value()      //=> '9px'
 *
 * // Value will default to zero if only a unit is provided:
 * cssValue('px').add(c).multiply(2).value() //=> '10px'
 *
 * // You can also typecast a unitless computation:
 * cssValue(2).add(2).add('px') //=> '4px'
 *
 *
 * @param  {*} val - String, number, or cssValue to wrap.
 * @return {object} - cssValue instance.
 */
export const cssValue = val => ({
  parse() {
    const [, value, unit] = val ? String(val.value ? val.value() : val).match(/([\-\d\.]+)?([^\d]*)$/) : [];
    return [Number(value || 0), unit || ''];
  },
  value() {
    return this.parse().join('');
  },
  unit() {
    return this.parse()[1];
  },
  add(val) {
    const unit = this.unit() !== '' ? this.unit() : cssValue(val).unit();
    return cssValue(`${this + cssValue(val)}${unit}`);
  },
  subtract(val) {
    const unit = this.unit() !== '' ? this.unit() : cssValue(val).unit();
    return cssValue(`${this - cssValue(val)}${unit}`);
  },
  multiply(val) {
    const unit = this.unit() !== '' ? this.unit() : cssValue(val).unit();
    return cssValue(`${this * cssValue(val)}${unit}`);
  },
  divide(val) {
    const unit = this.unit() !== '' ? this.unit() : cssValue(val).unit();
    return cssValue(`${this / cssValue(val)}${unit}`);
  },
  pow(val) {
    const unit = this.unit() !== '' ? this.unit() : cssValue(val).unit();
    return cssValue(`${Math.pow(this, cssValue(val))}${unit}`);
  },
  sqrt() {
    return cssValue(`${Math.sqrt(this)}${this.unit()}`);
  },
  eq(val) {
    return this + 0 === val + 0;
  },
  eqeq(val) {
    return this + 0 === val + 0 && this.unit() === cssValue(val).unit();
  },
  toString() {
    return this.parse()[0];
  }
});


/**
 * Provided a direction, base, and height, returns an object of CSS styles to
 * apply to an element to produce an isosceles triangle with the provided
 * dimensions.
 *
 * @param  {string} direction - One of 'up', 'down', 'left', 'right'.
 * @param  {number} base - Length of the arrow base.
 * @param  {number} height - Height of the arrow.
 * @return {object} - Styles.
 */
export function computeArrowStyle(direction, base, height) {
  // Sides.
  const A = cssValue(height);
  const B = cssValue(base).divide(2);
  const C = A.pow(2).add(B.pow(2)).sqrt();

  // Angles
  const a = radToDeg(Math.atan(A / B));
  const skew = -90 + 2 * a;
  const rotation = skew / 2 - 45;

  const baseStyles = {
    'box-sizing': 'border-box',
    'transform-origin': '0 0',
    'display': 'block',
    'z-index': '-1',
    'position': 'absolute',
    'width': C.value(),
    'height': C.value()
  };

  switch (direction) {
    case 'right':
      return Object.assign(baseStyles, {
        'clip-path': 'polygon(' + [
          `calc(100% + ${cssValue(ARROW_OFFSET_ADJUSTMENT).multiply('1.5px').value()}) 0px`,
          '100% 100%',
          `0px calc(100% + ${cssValue(ARROW_OFFSET_ADJUSTMENT).multiply('1.5px').value()})`
        ].join(', ') + ')',
        top: '50%',
        left: `calc(100% - ${A.add(ARROW_OFFSET_ADJUSTMENT).value()})`,
        transform: [
          `rotate(${rotation}deg)`,
          `skewX(${skew}deg)`,
          `scaleY(${Math.cos(degToRad(skew))})`
        ].join(' ')
      });
    case 'left':
      return Object.assign(baseStyles, {
        'clip-path': 'polygon(' + [
          `calc(100% - ${cssValue(ARROW_OFFSET_ADJUSTMENT).multiply('1.25px').value()}) 0px`,
          '0px 0px',
          `0px calc(100% - ${cssValue(ARROW_OFFSET_ADJUSTMENT).multiply('1.25px').value()})`
        ].join(', ') + ')',
        top: '50%',
        left: `calc(0% - ${A.subtract(ARROW_OFFSET_ADJUSTMENT).value()})`,
        transform: [
          `rotate(${rotation}deg)`,
          `skewX(${skew}deg)`,
          `scaleY(${Math.cos(degToRad(skew))})`
        ].join(' ')
      });
    case 'top':
      return Object.assign(baseStyles, {
        'clip-path': 'polygon(' + [
          `calc(100% - ${cssValue(ARROW_OFFSET_ADJUSTMENT).multiply('1.25px').value()}) 0px`,
          '0px 0px',
          `0px calc(100% - ${cssValue(ARROW_OFFSET_ADJUSTMENT).multiply('1.25px').value()})`
        ].join(', ') + ')',
        left: '50%',
        top: `calc(0% - ${A.subtract(ARROW_OFFSET_ADJUSTMENT).value()})`,
        transform: [
          `rotate(${rotation + 90}deg)`,
          `skewX(${skew}deg)`,
          `scaleY(${Math.cos(degToRad(skew))})`
        ].join(' ')
      });
    case 'bottom':
    default:
      return Object.assign(baseStyles, {
        'clip-path': 'polygon(' + [
          `calc(100% + ${cssValue(ARROW_OFFSET_ADJUSTMENT).multiply('0.75px').value()}) 0px`,
          '100% 100%',
          `0px calc(100% + ${cssValue(ARROW_OFFSET_ADJUSTMENT).multiply('0.75px').value()})`
        ].join(', ') + ')',
        left: '50%',
        top: `calc(100% - ${A.add(ARROW_OFFSET_ADJUSTMENT).value()})`,
        transform: [
          `rotate(${rotation + 90}deg)`,
          `skewX(${skew}deg)`,
          `scaleY(${Math.cos(degToRad(skew))})`
        ].join(' ')
      });
  }
}


/**
 * Provided two arrays of numbers (assumed to be ranges) returns an array
 * indicating the beginning and end of the overlap of the two ranges. If no
 * overlap is found, returns an empty array.
 *
 * @example
 *
 * calculateOverlap([40, 70, 100], [50, 90, 130]) //=> [50, 100]
 *
 * @param  {array} rangeA
 * @param  {array} rangeB
 * @return {array}
 */
export function calculateOverlap(rangeA, rangeB) {
  const toNumbers = xs => xs.map(x => parseFloat(x, 10));
  const sortArray = xs => xs.sort((a, b) => a > b);

  // Ensures a set consists of numbers and are well-formed.
  rangeA = sortArray(toNumbers(rangeA));
  rangeB = sortArray(toNumbers(rangeB));

  const startA = head(rangeA);
  const endA = last(rangeA);
  const startB = head(rangeB);
  const endB = last(rangeB);

  // Overlap start is the greater of the two starting points that is also lesser
  // than the other set's ending point.
  const overlapStart = startA > startB ? startA < endB ? startA : false
    : startB < endA ? startB : false;

  // Overlap end is the lesser of the two ending points that is also greater
  // than the other set's starting point.
  const overlapEnd = endA < endB ? endA > startB ? endA : false
    : endB > startA ? endB : false;

  if (overlapStart === false || overlapEnd === false) {
    return [];
  }

  return [overlapStart, overlapEnd];
}


/**
 * Provided a Tether position descriptor and a side, returns the
 * viewport-absolute start and end points of the side in terms of the relevant
 * top/left axis.
 *
 * See: http://tether.io/advanced/extending_tether/
 *
 * @example
 *
 * getAbsoluteRangeFromPos({
 *   ...
 *   left: 100,
 *   width: 100,
 *   ...
 * }, 'top') => [100, 200]
 *
 * @param  {object} pos - Tether position descriptor.
 * @param  {string} side - One of 'top', 'left', 'bottom', 'right'.
 * @return {array} - Absolute start and end points of the side.
 */
export function getAbsoluteRangeFromPos(pos, side) {
  switch (side) {
    case 'top':
    case 'bottom':
      return [pos.left, pos.left + pos.width];
    case 'left':
    case 'right':
      return [pos.top, pos.top + pos.height];
  }
}


/**
 * Provided two Tether position descriptors for an element and a reference
 * element, determines which side of the element overlaps with the complementary
 * side of the reference element.
 *
 * See: http://tether.io/advanced/extending_tether/
 *
 * @example
 *
 * calculateEdgeOverlap(elPos, refPos) //=> {'left', [0, 20]}
 *
 * @param  {object} elPos - Element position descriptor.
 * @param  {refPos} refPos - Reference position descriptor.
 * @return {object} - Object indicating the side and pixel range of overlap.
 */
export function calculateEdgeOverlap(elPos, refPos) {
  for (const edge of Object.keys(SIDES)) {
    const [dimension, offsetAxis] = SIDES[edge];

    if (Math.floor(refPos[edge] + refPos[dimension]) <= Math.ceil(elPos[edge])) {
      const elSide = getAbsoluteRangeFromPos(elPos, edge);
      const refSide = getAbsoluteRangeFromPos(refPos, edge);
      const overlap = calculateOverlap(elSide, refSide).map(length => length - elPos[offsetAxis]);

      return {
        edge,
        elSide,
        refSide,
        overlap
      };
    }
  }
}


/**
 * Determines if the configured attachment points will ensure that element edges
 * overlap. Non-overlapping edges may be desired in rare cases, but is likely
 * developer error.
 *
 * @param  {object} options - Popover or Tether options object.
 * @return {boolean}
 */
export function isEdgeOverlapAttachment({attachment, targetAttachment}) {
  for (const [el, target] of EDGE_OVERLAP_ATTACHMENTS) {
    if (el === attachment && target === targetAttachment) {
      return true;
    }
  }

  return false;
}


/**
 * Returns true if the provided point is bounded by the provided rectangle.
 *
 * @example
 *
 * isPointBoundedByRect({
 *   x: 200
 *   y: 450
 * }, {
 *   left: 100
 *   width: 200
 *   top: 150
 *   height: 500
 * }) //=> true
 *
 * @param  {object}  point
 * @param  {number}  point.x
 * @param  {number}  point.y
 * @param  {object}  rect
 * @param  {number}  rect.top
 * @param  {number}  rect.left
 * @param  {number}  rect.height
 * @param  {number}  rect.width
 * @return {boolean}
 */
export function isPointBoundedByRect({x, y}, {top, left, height, width}) {
  return x > left && x < left + width && y > top && y < top + height;
}


/**
 * Accepts a function and returns a function that may only be invoked with the
 * same arguments at a rate no greater than the provided rate.
 *
 * @param  {function} fn   - Function to throttle.
 * @param  {number}   rate - Rate (in ms) to cap invocations at.
 * @return {function} - Throttled version of the function.
 */
export function throttleMemoized(fn, rate) {
  const memo = {};
  const context = this;

  return function (...args) {
    const now = new Date().getTime();
    if (now - (memo[args] || 0) > rate) {
      memo[args] = now;
      return Reflect.apply(fn, context, args);
    }
  };
}



// WEBPACK FOOTER //
// ./lib/utils.js